import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import { Plus, Trash2, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const MyInvoice = () => {
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(true);

  const [invoiceData, setInvoiceData] = useState({
    customerName: '',
    customerEmail: '',
    customerMobile: '',
    carNumberPlate: '',
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    notes: '',
    items: [
      { description: '', quantity: 1, unitPrice: 0 }
    ]
  });

  // Fetch next invoice number on component mount
  React.useEffect(() => {
    const fetchInvoiceNumber = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/next-invoice-number`);
        const data = await response.json();
        if (data.success) {
          setInvoiceData(prev => ({
            ...prev,
            invoiceNumber: data.invoiceNumber
          }));
        }
      } catch (error) {
        console.error('Error fetching invoice number:', error);
        // Fallback to temporary number if API fails
        setInvoiceData(prev => ({
          ...prev,
          invoiceNumber: 'INV-TM0001'
        }));
      } finally {
        setLoadingInvoiceNumber(false);
      }
    };
    
    fetchInvoiceNumber();
  }, []);

  const addLineItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { description: '', quantity: 1, unitPrice: 0 }]
    });
  };

  const removeLineItem = (index) => {
    const newItems = invoiceData.items.filter((_, i) => i !== index);
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const calculateTotal = () => {
    return invoiceData.items.reduce((total, item) => {
      return total + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
    }, 0);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Add Think Mechanic Logo
    const logoUrl = 'https://customer-assets.emergentagent.com/job_think-mechanic/artifacts/glpenkiu_logo.png';
    try {
      doc.addImage(logoUrl, 'PNG', 20, 10, 50, 20);
    } catch (e) {
      // Fallback to text if image fails to load
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 38, 38);
      doc.text('THINK MECHANIC', 20, 20);
    }
    
    // Company details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text('German Car Repair Specialists', 20, 35);
    doc.text('Ras Al Khor Industrial Area 1, Dubai, UAE', 20, 40);
    doc.text('Phone: +971 56 667 1468', 20, 45);
    doc.text('Email: info@thinkmechanicdxb.com', 20, 50);
    doc.text('Website: www.thinkmechanicdxb.com', 20, 55);

    // Invoice title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('INVOICE', 200, 25, { align: 'right' });

    // Invoice details - Right aligned
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(`Invoice #: ${invoiceData.invoiceNumber}`, 200, 35, { align: 'right' });
    doc.text(`Date: ${invoiceData.invoiceDate}`, 200, 41, { align: 'right' });

    // Divider line
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(0.5);
    doc.line(20, 60, 190, 60);

    // Customer details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Bill To:', 20, 70);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(invoiceData.customerName, 20, 77);
    doc.text(invoiceData.customerEmail, 20, 83);
    if (invoiceData.customerMobile) {
      doc.text(`Mobile: ${invoiceData.customerMobile}`, 20, 89);
    }
    if (invoiceData.carNumberPlate) {
      doc.text(`Car Plate: ${invoiceData.carNumberPlate}`, 20, 95);
    }

    // Line items table
    const tableData = invoiceData.items.map(item => [
      item.description,
      item.quantity,
      `AED ${parseFloat(item.unitPrice).toFixed(2)}`,
      `AED ${(item.quantity * item.unitPrice).toFixed(2)}`
    ]);

    // Adjust startY based on how many customer details we have
    let tableStartY = 95;
    if (invoiceData.customerMobile) tableStartY += 6;
    if (invoiceData.carNumberPlate) tableStartY += 6;
    
    autoTable(doc, {
      startY: tableStartY,
      head: [['Description', 'Qty', 'Unit Price', 'Total']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38], textColor: 255 },
      styles: { fontSize: 10 }
    });

    // Grand total
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    const total = calculateTotal();
    doc.text(`Grand Total: AED ${total.toFixed(2)}`, 140, finalY);

    // Notes
    if (invoiceData.notes) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Notes:', 20, finalY + 15);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const splitNotes = doc.splitTextToSize(invoiceData.notes, 170);
      doc.text(splitNotes, 20, finalY + 22);
    }

    // Footer
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('Thank you for your business!', 105, 280, { align: 'center' });

    return doc;
  };

  const validateForm = () => {
    if (!invoiceData.customerName) {
      alert('Please fill in customer name');
      return false;
    }
    
    if (invoiceData.items.length === 0 || !invoiceData.items[0].description) {
      alert('Please add at least one line item');
      return false;
    }
    return true;
  };

  const handleDownloadPDF = () => {
    if (!validateForm()) return;
    
    const pdf = generatePDF();
    pdf.save(`${invoiceData.invoiceNumber}.pdf`);
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>My Invoice - Think Mechanic Dubai</title>
        <meta name="description" content="Generate and send professional invoices - Think Mechanic Dubai" />
      </Helmet>

      <Header />

      <section className="py-24 sm:py-20 bg-gradient-to-b from-black to-zinc-900 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                <FileText className="inline-block w-6 h-6 sm:w-8 sm:h-8 mr-2 text-red-600" />
                Invoice Generator
              </h1>
              <p className="text-sm sm:text-base text-gray-400">Create and send professional invoices</p>
            </div>

            {/* Invoice Form */}
            <div className="bg-zinc-900 rounded-xl p-4 sm:p-6 md:p-8 border border-zinc-800">
              {/* Customer Details */}
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div>
                  <label className="block text-white font-semibold mb-2 text-sm sm:text-base">Customer Name *</label>
                  <input
                    type="text"
                    value={invoiceData.customerName}
                    onChange={(e) => setInvoiceData({ ...invoiceData, customerName: e.target.value })}
                    className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-red-600"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Customer Email</label>
                  <input
                    type="email"
                    value={invoiceData.customerEmail}
                    onChange={(e) => setInvoiceData({ ...invoiceData, customerEmail: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                    placeholder="customer@email.com"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Customer Mobile#</label>
                  <input
                    type="tel"
                    value={invoiceData.customerMobile}
                    onChange={(e) => setInvoiceData({ ...invoiceData, customerMobile: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                    placeholder="+971 50 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-white font-semibold mb-2">Car Number Plate#</label>
                  <input
                    type="text"
                    value={invoiceData.carNumberPlate}
                    onChange={(e) => setInvoiceData({ ...invoiceData, carNumberPlate: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                    placeholder="Dubai A-12345"
                  />
                </div>
              </div>

              {/* Invoice Details */}
              <div className="mb-8">
                {/* Invoice Number - Full width */}
                <div className="mb-4">
                  <label className="block text-white font-semibold mb-2">Invoice Number</label>
                  <input
                    type="text"
                    value={loadingInvoiceNumber ? 'Loading...' : invoiceData.invoiceNumber}
                    readOnly
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-gray-400"
                  />
                </div>

                {/* Invoice Date - Half width to make it compact */}
                <div className="w-1/2 pr-2">
                  <label className="block text-white font-semibold mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceData.invoiceDate}
                    onChange={(e) => setInvoiceData({ ...invoiceData, invoiceDate: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                  />
                </div>
              </div>

              {/* Job Description */}
              <div className="mb-8">
                <label className="block text-white font-semibold mb-4">Job Description</label>
                
                {invoiceData.items.map((item, index) => (
                  <div key={index} className="mb-6 p-4 bg-zinc-800 border border-zinc-700 rounded-lg">
                    {/* Description */}
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2">Description</label>
                      <input
                        type="text"
                        placeholder="Service description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                      />
                    </div>

                    {/* Qty */}
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2">Qty</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                      />
                    </div>

                    {/* Price */}
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2">Price (AED)</label>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                        className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                      />
                    </div>

                    {/* Total */}
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2">Total</label>
                      <div className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-gray-400">
                        AED {(item.quantity * item.unitPrice).toFixed(2)}
                      </div>
                    </div>

                    {/* Delete button */}
                    {invoiceData.items.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all flex items-center justify-center space-x-2"
                      >
                        <Trash2 className="w-5 h-5" />
                        <span>Remove Item</span>
                      </button>
                    )}
                  </div>
                ))}

                <button
                  onClick={addLineItem}
                  className="flex items-center space-x-2 text-red-600 hover:text-red-500 font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  <span>Add Line Item</span>
                </button>
              </div>

              {/* Grand Total */}
              <div className="flex justify-end mb-8">
                <div className="bg-red-600 px-8 py-4 rounded-lg">
                  <p className="text-white text-sm mb-1">Grand Total</p>
                  <p className="text-white text-3xl font-bold">
                    AED {calculateTotal().toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-8">
                <label className="block text-white font-semibold mb-2">Notes (Optional)</label>
                <textarea
                  value={invoiceData.notes}
                  onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-red-600"
                  placeholder="Any additional notes or terms..."
                />
              </div>

              {/* Download PDF Button */}
              <div className="space-y-4">
                <Button
                  onClick={handleDownloadPDF}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold rounded-lg"
                >
                  <FileText className="mr-2 w-5 h-5" />
                  Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
